/**
 * Expression compilation and sampling.
 *
 * mathjs parses; we lock it down and sample it. Two concerns dominate:
 * safety (a model-authored string is untrusted input, and mathjs can otherwise
 * reach `import`/`createUnit`) and honesty about discontinuities — a 1/x plot
 * must show a gap at the origin, not a vertical line joining +∞ to −∞.
 */

import { create, all, type MathNode } from "mathjs";

// `all` is typed as possibly-undefined by mathjs; it never is at runtime.
const math = create(all!, {});

/**
 * Hold onto the real parser *before* locking the namespace down. The stubs
 * below make these names unreachable from inside a model-authored expression,
 * which is the point — but we still need to parse, so we keep a private
 * reference the sandboxed scope can't see.
 */
const parseExpression: (expr: string) => MathNode = math.parse.bind(math);

// Untrusted input: everything that can reach the host or mutate the
// environment becomes a throwing stub. Calls to them surface as NaN.
const disabled = () => {
  throw new Error("this function is not available in plot expressions");
};

math.import(
  {
    import: disabled,
    createUnit: disabled,
    evaluate: disabled,
    parse: disabled,
    simplify: disabled,
    derivative: disabled,
  },
  { override: true },
);

export type CompiledExpression = (scope: Record<string, number>) => number;

export type CompileResult =
  | { readonly ok: true; readonly fn: CompiledExpression }
  | { readonly ok: false; readonly error: string };

/**
 * Compile once, evaluate thousands of times. Anything that doesn't come back
 * as a finite real number becomes NaN, which the renderer draws as a break.
 */
export function compileExpression(expr: string): CompileResult {
  let node: MathNode;
  try {
    node = parseExpression(expr);
  } catch (cause) {
    return { ok: false, error: readError(cause) };
  }

  let compiled: { evaluate: (scope?: object) => unknown };
  try {
    compiled = node.compile();
  } catch (cause) {
    return { ok: false, error: readError(cause) };
  }

  const fn: CompiledExpression = (scope) => {
    try {
      return toRealNumber(compiled.evaluate(scope));
    } catch {
      return Number.NaN;
    }
  };

  return { ok: true, fn };
}

/**
 * mathjs happily returns complex numbers (sqrt(-1)), BigNumbers, and units.
 * A 2-D plot can only draw a real scalar, so everything else is a hole.
 */
function toRealNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : Number.NaN;
  if (typeof value === "boolean") return value ? 1 : 0;

  if (value !== null && typeof value === "object") {
    const record = value as { re?: unknown; im?: unknown; toNumber?: () => number };
    // Complex with a negligible imaginary part is a rounding artefact, not a value.
    if (typeof record.re === "number" && typeof record.im === "number") {
      return Math.abs(record.im) < 1e-12 ? record.re : Number.NaN;
    }
    if (typeof record.toNumber === "function") {
      const asNumber = record.toNumber();
      return Number.isFinite(asNumber) ? asNumber : Number.NaN;
    }
  }

  return Number.NaN;
}

function readError(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * Free *variables* in an expression — the names that must be supplied by the
 * plot's variable or its sliders.
 *
 * Function names are excluded (mathjs parses the `sin` in `sin(x)` as a symbol
 * of its own, and reporting it as undefined would fail nearly every figure),
 * as are the maths constants. A name that is neither is a genuine dependency,
 * or a typo — and the caller treats both the same way, which is what we want.
 */
export function symbolsIn(expr: string): readonly string[] {
  try {
    const node = parseExpression(expr);
    const found = new Set<string>();

    node.forEach(function walk(child: MathNode) {
      collect(child, found);
      child.forEach(walk);
    });
    collect(node, found);

    return [...found];
  } catch {
    return [];
  }
}

function collect(node: MathNode, found: Set<string>): void {
  if (node.type !== "SymbolNode") return;
  const name = (node as MathNode & { name: string }).name;
  if (isBuiltIn(name)) return;
  found.add(name);
}

/**
 * Callables and constants provided by mathjs. Numeric constants that double as
 * ordinary physics symbols (`E` for energy or field) are deliberately *not*
 * treated as built-in: silently reading 2.718 where a reader meant a field is
 * worse than an "undefined symbol" message. A declared slider of the same name
 * shadows the built-in anyway, so nothing legitimate is blocked.
 */
function isBuiltIn(name: string): boolean {
  if (KNOWN_CONSTANTS.has(name)) return true;

  const namespace = math as unknown as Record<string, unknown>;
  return (
    Object.prototype.hasOwnProperty.call(namespace, name) &&
    typeof namespace[name] === "function"
  );
}

const KNOWN_CONSTANTS = new Set(["pi", "PI", "e", "tau", "phi", "Infinity", "NaN", "i"]);

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * A path may be broken into several segments by singularities. Keeping them
 * separate is what stops the renderer from drawing a fake asymptote.
 */
export type Segments = readonly (readonly Point[])[];

export interface SampleOptions {
  readonly from: number;
  readonly to: number;
  readonly samples: number;
  /** Log-x plots need geometric spacing or the left half is wasted. */
  readonly logSpacing?: boolean;
}

/** Sample y = f(t) over a range, returning connected segments. */
export function sampleFunction(
  fn: CompiledExpression,
  variable: string,
  scope: Readonly<Record<string, number>>,
  options: SampleOptions,
): Segments {
  const points: (Point | null)[] = [];
  const working: Record<string, number> = { ...scope };

  for (let i = 0; i < options.samples; i++) {
    const t = positionAt(i, options);
    working[variable] = t;
    const y = fn(working);
    points.push(Number.isFinite(y) ? { x: t, y } : null);
  }

  return splitSegments(points);
}

/** Sample (x(t), y(t)) over a parameter range. */
export function sampleParametric(
  xFn: CompiledExpression,
  yFn: CompiledExpression,
  parameter: string,
  scope: Readonly<Record<string, number>>,
  options: SampleOptions,
): Segments {
  const points: (Point | null)[] = [];
  const working: Record<string, number> = { ...scope };

  for (let i = 0; i < options.samples; i++) {
    const t = positionAt(i, options);
    working[parameter] = t;
    const x = xFn(working);
    const y = yFn(working);
    points.push(Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null);
  }

  return splitSegments(points);
}

function positionAt(index: number, options: SampleOptions): number {
  const fraction = options.samples === 1 ? 0 : index / (options.samples - 1);

  if (options.logSpacing && options.from > 0 && options.to > 0) {
    const logFrom = Math.log(options.from);
    const logTo = Math.log(options.to);
    return Math.exp(logFrom + fraction * (logTo - logFrom));
  }

  return options.from + fraction * (options.to - options.from);
}

function splitSegments(points: readonly (Point | null)[]): Segments {
  const segments: Point[][] = [];
  let current: Point[] = [];

  for (const point of points) {
    if (point === null) {
      if (current.length > 0) segments.push(current);
      current = [];
      continue;
    }
    current.push(point);
  }
  if (current.length > 0) segments.push(current);

  return segments;
}

/**
 * Split a segment wherever it jumps implausibly far in y — the signature of a
 * pole that happened to land between two samples (tan x, 1/(x-a)).
 */
export function splitOnPoles(
  segments: Segments,
  yRange: readonly [number, number],
): Segments {
  const threshold = Math.abs(yRange[1] - yRange[0]) * 2;
  if (!Number.isFinite(threshold) || threshold === 0) return segments;

  const output: Point[][] = [];

  for (const segment of segments) {
    let current: Point[] = [];
    for (let i = 0; i < segment.length; i++) {
      const point = segment[i]!;
      const previous = segment[i - 1];

      if (previous && Math.abs(point.y - previous.y) > threshold) {
        // A sign flip across a big jump is a pole; a same-sign jump is just steep.
        if (Math.sign(point.y) !== Math.sign(previous.y)) {
          if (current.length > 0) output.push(current);
          current = [];
        }
      }
      current.push(point);
    }
    if (current.length > 0) output.push(current);
  }

  return output;
}
